import { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { fbt } from 'fbtee'
import { isAndroidBrowser, isMobileBrowser } from '@/lib/platform'
import { track } from '@/lib/analytics'
import { BottomBanner } from './BottomBanner'

const STORAGE_KEY = 'beacon:app_upsell'
const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS
// How long the banner stays hidden after the 1st, 2nd, 3rd… dismissal.
// A first dismissal costs hours rather than the whole pre-festival window —
// people research a lineup over 2-3 weeks, and a flat week of silence ate most
// of it. Repeated dismissals back off hard. The final step replaces what used
// to be permanent suppression: Beacon is seasonal, so a "no" in August
// shouldn't still be binding on-site the following July.
const DISMISS_COOLDOWNS_MS = [
  12 * HOUR_MS,
  1 * DAY_MS,
  3 * DAY_MS,
  7 * DAY_MS,
  14 * DAY_MS,
  90 * DAY_MS,
]
const IOS_STORE_URL = 'https://apps.apple.com/us/app/beacon-festival/id6777554943'
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=app.beacon.festival'

function getStorePlatform(): 'ios' | 'android' {
  return isAndroidBrowser() ? 'android' : 'ios'
}

function getStoreUrl(platform: 'ios' | 'android'): string {
  return platform === 'android' ? ANDROID_STORE_URL : IOS_STORE_URL
}

function readDismissState(): { count: number; until: number } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { count: 0, until: 0 }
    const parsed = JSON.parse(raw)
    return {
      count: Number(parsed?.count) || 0,
      // Coerced because rows written before the backoff ladder stored
      // `until: Infinity`, which JSON.stringify turns into null.
      until: Number(parsed?.until) || 0,
    }
  } catch {
    return { count: 0, until: 0 }
  }
}

function cooldownFor(dismissals: number): number {
  const index = Math.min(dismissals, DISMISS_COOLDOWNS_MS.length) - 1
  return DISMISS_COOLDOWNS_MS[Math.max(index, 0)]
}

function isSuppressed(): boolean {
  return readDismissState().until > Date.now()
}

function persistDismiss() {
  try {
    const { count } = readDismissState()
    const next = count + 1
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      count: next,
      until: Date.now() + cooldownFor(next),
    }))
  } catch {
    // Private mode / quota — banner will reappear next session, acceptable.
  }
}

export function AppInstallBanner() {
  // Pinned at mount: the suppression window and the UA verdict must not change
  // under the user mid-session.
  const [platform] = useState(getStorePlatform)
  const [shouldRender] = useState(() => isMobileBrowser() && !isSuppressed())
  const [visible, setVisible] = useState(shouldRender)
  const [animateIn, setAnimateIn] = useState(false)
  const tracked = useRef(false)

  useEffect(() => {
    if (!visible) return
    const raf = requestAnimationFrame(() => setAnimateIn(true))
    return () => cancelAnimationFrame(raf)
  }, [visible])

  useEffect(() => {
    if (visible && !tracked.current) {
      tracked.current = true
      track('app_install_banner_shown', { platform })
    }
  }, [visible, platform])

  const dismiss = useCallback(() => {
    setAnimateIn(false)
    setTimeout(() => {
      setVisible(false)
      persistDismiss()
      track('app_install_banner_dismissed', { platform })
    }, 200)
  }, [platform])

  if (!visible) return null

  return (
    <BottomBanner
      className="md:hidden transition-transform duration-200 ease-out"
      style={{ transform: animateIn ? 'translateY(0)' : 'translateY(100%)' }}
    >
        <img src="/beacon-icon-192.png" alt="" className="size-9 flex-none rounded-lg" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold leading-tight truncate">Faro</div>
          <div className="text-[11px] text-muted-foreground leading-tight truncate">
            {fbt(
              'Open in the app',
              'Subtitle in the app install banner encouraging users to use the native app',
            )}
          </div>
        </div>
        <a
          href={getStoreUrl(platform)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track('app_install_banner_tapped', { platform })}
          className="flex-none rounded-full bg-primary px-3.5 py-1 text-xs font-bold uppercase tracking-wide text-primary-foreground hover:bg-primary/90"
        >
          {fbt('Get', 'Button label to download the app from the store in the install banner')}
        </a>
        <button
          type="button"
          onClick={dismiss}
          className="flex-none text-muted-foreground hover:text-foreground cursor-pointer -mr-1.5 p-1"
          aria-label={fbt('Dismiss', 'Accessible label for the close button on the app install banner')}
        >
          <X className="size-3.5" />
        </button>
    </BottomBanner>
  )
}
